import { FunctionComponent, MouseEvent, useState } from "react";
import { useTranslation } from "react-i18next";

import {
	faCheck,
	faChevronDown,
	faChevronUp,
	faDatabase,
	faEye,
	faFlag,
	faList,
	faPlus,
} from "@fortawesome/pro-solid-svg-icons";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { faEuroSign, faWebhook } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { classNames } from "@/utils/classNames";

import { CrmProvider } from "@/pages/Private/redux/crm/crm.schema";
import { ReachOutCampaignPicker } from "@/pages/Private/pages/List/components/ReachOut/ReachOutCampaignPicker";
import { ListPerson } from "@/pages/Private/pages/List/schema/list";
import { useWebhooks } from "@/pages/Private/pages/List/utils/useWebhooks";

import { AnchoredPopover } from "./AnchoredPopover";
import { TrackerPickerPanel } from "./TrackerPickerPanel";
import { SelectListPopover } from "../SelectListPopover";
import { LeadMembership } from "./leadPanelActions.utils";
import { LeadPanelActionsApi } from "./useLeadPanelActions";

type AddToView = "root" | "list" | "tracker" | "campaign" | "crm" | "deal";

const CRM_PROVIDER_LABEL: Record<CrmProvider, string> = {
	[CrmProvider.HUBSPOT]: "HubSpot",
	[CrmProvider.ZOHO]: "Zoho",
	[CrmProvider.PIPEDRIVE]: "Pipedrive",
	[CrmProvider.MSDYNAMICS]: "Dynamics 365",
	[CrmProvider.SALESFORCE]: "Salesforce",
};

interface AddToMenuProps {
	/** The resolved `ListPerson`; `null` when the lead is on no list (campaign/webhook disabled). */
	listPerson: ListPerson | null;
	membership: LeadMembership;
	connectedCrmProviders: CrmProvider[];
	portalContainer?: HTMLElement | null;
	actions: Pick<
		LeadPanelActionsApi,
		"addToList" | "addToTracker" | "isAddingToList" | "isAddingToTracker"
	>;
	/**
	 * Receives the picked CRM provider — the host opens its add-to-CRM flow (the web surfaces open
	 * the shared `AddToCrmModal` via `useAddToCrmModal`; the extension commits directly). Required
	 * so no host silently loses the CRM row.
	 */
	onAddToCrm: (provider: CrmProvider) => void;
	/** Hide the "Add to campaign" row (the Inbox action bar shows only list/tracker/webhook/CRM/deal). */
	showCampaign?: boolean;
	/** Which side of the trigger the menu opens on — `"top"` for bottom-sticky bars. */
	placement?: "bottom" | "top";
	/** Override for the trigger button label; defaults to the lead-panel "Add to" label. */
	triggerLabel?: string;
	/** Class of the root wrapper — the lead panel stretches (`flex-1`), the Inbox bar fits content. */
	className?: string;
	/**
	 * CRM providers a deal can be created in. Together with `onCreateDeal`, adds a "Create deal" row
	 * (direct call for a single provider, provider sub-menu for several). Omit to hide the row.
	 */
	dealProviders?: CrmProvider[];
	onCreateDeal?: (provider: CrmProvider) => void;
}

interface AddToRow {
	key: string;
	icon: IconDefinition;
	label: string;
	checked: boolean;
	disabled: boolean;
	disabledHint?: string;
	onClick: () => void;
}

/**
 * The "Add to ▾" split-button + its dropdown. Each row flips to a checked "Added to …" state when
 * the lead already belongs to that destination. List / Tracker / CRM act on the prospect directly;
 * Campaign and Webhook require a `ListPerson` and are disabled (with a hint) when the lead is on no
 * list. Sub-pickers are hosted inside the same anchored popover; the webhook picker is a MUI modal.
 * The CRM row hands the picked provider to the host via `onAddToCrm` (direct call for a single
 * connected provider, provider sub-menu for several) — the host owns the follow-up, normally the
 * shared `AddToCrmModal`. An optional "Create deal" row (`dealProviders` + `onCreateDeal`) works
 * the same way for the host's CRM deal modals — used by the Inbox action bar, which also hides the
 * campaign row (`showCampaign`).
 */
export const AddToMenu: FunctionComponent<AddToMenuProps> = ({
	listPerson,
	membership,
	connectedCrmProviders,
	portalContainer,
	actions,
	onAddToCrm,
	showCampaign = true,
	placement = "bottom",
	triggerLabel,
	className = "flex-1",
	dealProviders = [],
	onCreateDeal,
}) => {
	const { t } = useTranslation();
	const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
	const [view, setView] = useState<AddToView>("root");

	const webhooks = useWebhooks();

	const isOpen = !!anchorEl;
	const hasListPerson = !!listPerson;
	const noListHint = t("contacts.leadPanel.actions.addTo.needsListHint");

	const close = () => {
		setAnchorEl(null);
		setView("root");
	};

	const toggle = (event: MouseEvent<HTMLButtonElement>) => {
		event.stopPropagation();
		if (isOpen) {
			close();
		} else {
			setView("root");
			setAnchorEl(event.currentTarget);
		}
	};

	const handleAddToCrm = () => {
		if (connectedCrmProviders.length === 1) {
			onAddToCrm(connectedCrmProviders[0]);
			close();
		} else {
			setView("crm");
		}
	};

	const handleCreateDeal = () => {
		if (!onCreateDeal) {
			return;
		}

		if (dealProviders.length === 1) {
			onCreateDeal(dealProviders[0]);
			close();
		} else {
			setView("deal");
		}
	};

	const rows: AddToRow[] = [
		{
			key: "list",
			icon: faList,
			label: membership.inList
				? t("contacts.leadPanel.actions.addTo.addedToList")
				: t("contacts.leadPanel.actions.addTo.list"),
			checked: membership.inList,
			disabled: false,
			onClick: () => setView("list"),
		},
		{
			key: "campaign",
			icon: faFlag,
			label: listPerson?.campaignId
				? t("contacts.leadPanel.actions.addTo.addedToCampaign")
				: t("contacts.leadPanel.actions.addTo.campaign"),
			checked: !!listPerson?.campaignId,
			disabled: !hasListPerson,
			disabledHint: noListHint,
			onClick: () => setView("campaign"),
		},
		{
			key: "tracker",
			icon: faEye,
			label: membership.inTracker
				? t("contacts.leadPanel.actions.addTo.addedToTracker")
				: t("contacts.leadPanel.actions.addTo.tracker"),
			checked: membership.inTracker,
			disabled: false,
			onClick: () => setView("tracker"),
		},
		{
			key: "crm",
			icon: faDatabase,
			label: membership.inCrm
				? t("contacts.leadPanel.actions.addTo.addedToCrm")
				: t("contacts.leadPanel.actions.addTo.crm"),
			checked: membership.inCrm,
			disabled: false,
			onClick: handleAddToCrm,
		},
		{
			key: "deal",
			icon: faEuroSign,
			label: t("contacts.leadPanel.actions.addTo.createDeal"),
			checked: false,
			disabled: false,
			onClick: handleCreateDeal,
		},
		{
			key: "webhook",
			icon: faWebhook,
			label: t("contacts.leadPanel.actions.addTo.webhook"),
			checked: false,
			disabled: !hasListPerson,
			disabledHint: noListHint,
			onClick: () => {
				webhooks.setShowWebhookModal(true);
				close();
			},
		},
	]
		.filter((row) => row.key !== "campaign" || showCampaign)
		.filter((row) => row.key !== "crm" || connectedCrmProviders.length > 0)
		.filter((row) => row.key !== "deal" || (dealProviders.length > 0 && !!onCreateDeal));

	const commitWebhook = async () => {
		if (!listPerson || !webhooks.selectedWebhook) {
			return;
		}

		await webhooks
			.bulkAddToWebhook({
				webhookId: Number(webhooks.selectedWebhook.id),
				listPersonIds: [listPerson.id],
			})
			.unwrap();
	};

	const renderRoot = () => (
		<div
			className="overflow-hidden rounded-lg border border-gray-200 bg-white py-1 shadow-lg"
			data-testid="lead-panel-addto-menu"
		>
			{rows.map((row) => (
				<button
					key={row.key}
					className={classNames(
						"flex w-full cursor-pointer items-center gap-2.5 px-3 py-2 text-left text-sm duration-150 disabled:cursor-not-allowed disabled:opacity-40",
						row.checked ? "text-brand-light" : "text-gray-900 hover:bg-gray-100"
					)}
					data-testid={`lead-panel-addto-${row.key}`}
					disabled={row.disabled}
					title={row.disabled ? row.disabledHint : undefined}
					type="button"
					onClick={row.onClick}
				>
					<Icon className="w-4 shrink-0" icon={row.checked ? faCheck : row.icon} />
					<span className="truncate">{row.label}</span>
				</button>
			))}
		</div>
	);

	const renderProviderList = (
		providers: CrmProvider[],
		icon: IconDefinition,
		onPick: (provider: CrmProvider) => void
	) => (
		<div className="overflow-hidden rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
			{providers.map((provider) => (
				<button
					key={provider}
					className="flex w-full cursor-pointer items-center gap-2.5 px-3 py-2 text-left text-sm text-gray-900 duration-150 hover:bg-gray-100"
					type="button"
					onClick={() => {
						onPick(provider);
						close();
					}}
				>
					<Icon className="w-4 shrink-0" icon={icon} />
					<span className="truncate">{CRM_PROVIDER_LABEL[provider]}</span>
				</button>
			))}
		</div>
	);

	const renderPopoverBody = () => {
		if (view === "list") {
			return (
				<SelectListPopover
					isLoading={actions.isAddingToList}
					onCreateNew={(name) => {
						void actions.addToList({ listName: name });
						close();
					}}
					onSelectList={(list) => {
						void actions.addToList({ listId: list.id });
						close();
					}}
				/>
			);
		}

		if (view === "tracker") {
			return (
				<TrackerPickerPanel
					isLoading={actions.isAddingToTracker}
					onSelectTracker={(tracker) => {
						void actions.addToTracker(tracker.id);
						close();
					}}
				/>
			);
		}

		if (view === "campaign" && listPerson) {
			return (
				<div className="max-h-[70vh] overflow-y-auto rounded-lg border border-gray-200 bg-white p-3 shadow-lg">
					<ReachOutCampaignPicker target={{ kind: "lead", row: listPerson }} onDone={close} />
				</div>
			);
		}

		if (view === "crm") {
			return renderProviderList(connectedCrmProviders, faDatabase, (provider) => {
				onAddToCrm(provider);
			});
		}

		if (view === "deal") {
			return renderProviderList(dealProviders, faEuroSign, (provider) => {
				onCreateDeal?.(provider);
			});
		}

		return renderRoot();
	};

	return (
		<div className={className}>
			<button
				className={classNames(
					"flex h-8 w-full cursor-pointer items-center justify-center gap-1.5 rounded-[10px] border border-gray-200 bg-white px-2.5 text-xs font-semibold text-text duration-200 hover:shadow-md"
				)}
				data-testid="lead-panel-addto-trigger"
				type="button"
				onClick={toggle}
			>
				<Icon className="w-3" icon={faPlus} />
				{triggerLabel ?? t("contacts.leadPanel.actions.addTo.trigger")}
				<Icon className="w-2.5" icon={placement === "top" ? faChevronUp : faChevronDown} />
			</button>

			{isOpen && (
				<AnchoredPopover
					anchorEl={anchorEl}
					placement={placement}
					portalContainer={portalContainer}
					width={view === "campaign" ? 360 : 288}
					onClose={close}
				>
					{renderPopoverBody()}
				</AnchoredPopover>
			)}

			{webhooks.renderModal(
				() => {
					void commitWebhook();
				},
				false,
				() => {}
			)}
		</div>
	);
};
